<template>
	<modal-side-generic
	sizeClass="big"
	:title="t('cards.transfers.flex.reuseModal.title')"
	:triggerClose="triggerClose"
	:tabs="reuseModalTabsList"
	@tabClicked="selectedTab = $event"
	@hideMe="[emit('hideMe'), triggerClose = false]">
		<!-- Loader -->
		<loader-generic v-if="showLoader || showOverlaySpinner" :fullSize="!showOverlaySpinner" :isOverlay="showOverlaySpinner"/>
		<!-- Error State -->
		<empty-state-error v-if="applyErrorState && !showLoader" :message="t('genericErrorMessage')"/>
		<!-- Tables -->
		<transition name="common-enter-animation" appear mode="out-in">
			<!-- Latest -->
			<div v-if="!showLoader && !applyErrorState && latestTransfersList.length > 0 && selectedTab === 'latest'" class="content-wrapper prevent-auto-margins">
				<table-generic
				:headerData="latestTransfersHeaders"
				:tableData="latestTransfersList"
				:tableStyles="latestTransfersStyles"
				@sortBy="transfersSortByHandler"
				@rowClicked="rowClickHandler($event)"/>
			</div>
			<!-- Favorites -->
			<div v-else-if="!showLoader && !applyErrorState && favoritesTableData.length > 0 && selectedTab === 'favorites'" class="content-wrapper prevent-auto-margins">
				<table-generic
				:headerData="favoritesHeaderData"
				:tableData="favoritesTableData"
				:tableStyles="favoritesTableStyles"
				:hasMore="getFavoritesHasMore"
				:isLoadingMore="tableLoadingMore"
				:loadingMoreText="t('cards.transfers.flex.reuseModal.loadingMoreText')"
				@loadMore="loadMoreHandler"
				@rowClicked="rowClickHandler($event)"/>
			</div>
		</transition>
		<!-- Empty States -->
		<empty-state-generic
		v-if="!showLoader && !applyErrorState &&
			((selectedTab === 'latest' && !latestTransfersList.length) ||
			(selectedTab === 'favorites' && !favoritesTableData.length))"
		:illustration="emptyStateParams.illustration"
		:title="emptyStateParams.title"
		:description="emptyStateParams.description"/>
	</modal-side-generic>
	<!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Aux */
import { sortTableData } from '@/assets/scripts/parsers/tables'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { getDate } from '@/assets/scripts/dateBuilders'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestFavoritesList } = mapActions(moduleMapper.CARDS_CHARGE)
const { getFavoritesList, getFavoritesHasMore } = mapGetters(moduleMapper.CARDS_CHARGE)

/* Emmitters */
const emit = defineEmits(['hideMe', 'reuseTransferRowClicked'])

/* Show Data */
const showLoader = ref(false)
const showOverlaySpinner = ref(false)

/* Data */
const triggerClose = ref(false)
const applyErrorState = ref(false)

/** Props
@getLastTransfers {Array}
@getCardsList {Array}
@isMobile {Boolean}
*/

const props = defineProps({
	getLastTransfers: Array,
	getCardsList: Array,
	isMobile: Boolean
})
/*************************************************
*                Reuse Modal Tabs                *
*************************************************/
const selectedTab = ref('latest')

const reuseModalTabsList = computed(() => {
	const tabs = [
		{ id: 'latest', label: t('cards.transfers.flex.reuseModal.tabsList.latest') },
		{ id: 'favorites', label: t('cards.transfers.flex.reuseModal.tabsList.favorites') }
	]

	return tabs.map(tab => {
		return { ...tab, selected: selectedTab.value === tab.id }
	})
})

const rowClickHandler = event => {
	let selectedRowData = {}

	if (selectedTab.value === 'latest') {
		selectedRowData = {
			originCard: event.rowData.find(params => params.dataAttr === 'originCard').content,
			destinationCard: event.rowData.find(params => params.dataAttr === 'destinationCard').content,
			amount: event.rowData.find(params => params.dataAttr === 'amount').value.content,
			currency: event.rowData.find(params => params.dataAttr === 'amount').value.currency
		}
	} else {
		const favoritesData = getFavoritesList.value.find(favorite => favorite.id === event.rowId).customerOperationView.parameters

		selectedRowData = {
			originCard: favoritesData.find(field => field.name === 'originCardId').value,
			destinationCard: favoritesData.find(field => field.name === 'destinationCardId').value,
			amount: parseFloat(favoritesData.find(field => field.name === 'amount').value),
			currenty: ACCOUNT_MAIN_CURRENCY
		}
	}

	emit('reuseTransferRowClicked', selectedRowData)
	emit('hideMe')
}

const emptyStateParams = computed(() => {
	if (selectedTab.value === 'latest') {
		return {
			illustration: 'emptyState/no-movements',
			title: t('cards.transfers.flex.reuseModal.emptyState.lastTransfers.title'),
			description: t('cards.transfers.flex.reuseModal.emptyState.lastTransfers.description')
		}
	} else {
		return {
			illustration: 'emptyState/favorites-folder',
			title: t('cards.transfers.flex.reuseModal.emptyState.favorites.title'),
			description: t('cards.transfers.flex.reuseModal.emptyState.favorites.description')
		}
	}
})
/*************************************************
*                Last Charges Data               *
*************************************************/
const latestTransfersList = ref(null)

const latestTransfersStyles = ref([
	{ width: '150px', 'min-width': '150px' },
	{ 'text-align': 'end' },
	{ width: '150px', 'min-width': '150px' },
	null
])

const latestTransfersHeaders = ref([
	{ label: t('cards.transfers.flex.reuseModal.originCard'), dataAttr: 'originCard', showInMobile: { groupWith: 'amount' }, rowCellType: 'title', sortable: true },
	{ label: t('cards.transfers.flex.reuseModal.amount'), dataAttr: 'amount', rowCellType: 'amount', sortable: true },
	{ label: t('cards.transfers.flex.reuseModal.destinationCard'), dataAttr: 'destinationCard', sortable: true },
	{ label: t('cards.transfers.flex.reuseModal.date'), dataAttr: 'date', initialSortDirection: 'Desc', sortable: true }
])

const parsedlatestTransfersList = () => {
	latestTransfersList.value = props.getLastTransfers.map(transfer => ([
		{ type: 'title', value: transfer.codedOriginCardNumber, dataAttr: 'originCard', content: transfer.originCardNumber },
		{ type: 'amount', value: { content: transfer.amount, currency: ACCOUNT_MAIN_CURRENCY, noBalanceIcon: true }, dataAttr: 'amount' },
		{ value: transfer.codedDestinationCardNumber, dataAttr: 'destinationCard', content: transfer.destinationCardNumber },
		{ value: { content: getDate(transfer.operationDate), classes: 'capitalize', rawDate: transfer.operationDate }, dataAttr: 'date' }
	]))
}

const transfersSortByHandler = (event) => {
	latestTransfersList.value = sortTableData(latestTransfersList.value, event)
}
/*************************************************
*                 Favorites Data                 *
*************************************************/
const favoritesList = ref(null)

const favoritesTableStyles = ref([
	null,
	{ width: '150px', 'min-width': '150px' },
	{ 'text-align': 'end' },
	{ width: '150px', 'min-width': '150px' }
])

const favoritesHeaderData = ref([
	{ label: t('cards.transfers.flex.reuseModal.name'), dataAttr: 'alias', showInMobile: { groupWith: 'amount' }, rowCellType: 'title', sortable: false },
	{ label: t('cards.transfers.flex.reuseModal.originCard'), dataAttr: 'originCard' },
	{ label: t('cards.transfers.flex.reuseModal.amount'), dataAttr: 'amount', rowCellType: 'amount' },
	{ label: t('cards.transfers.flex.reuseModal.destinationCard'), dataAttr: 'destinationCard' }
])

const favoritesTableData = computed(() => {
	if (!favoritesList.value) return []

	return favoritesList.value.map(favorite => {
		const params = favorite.customerOperationView.parameters
		const amount = getParameterValue(params, 'amount')
		const originCardId = getParameterValue(params, 'originCardId')
		const destinationCardId = getParameterValue(params, 'destinationCardId')

		return [
			{ value: favorite.id, dataAttr: 'id', initialSortDirection: 'Desc' },
			{ type: 'title', value: favorite.alias && favorite.alias.length ? favorite.alias : '-', dataAttr: 'alias' },
			{ value: getCodedCardNumber(originCardId) ? getCodedCardNumber(originCardId) : '-', dataAttr: 'originCard' },
			{ type: 'amount', value: { content: parseFloat(amount), currency: ACCOUNT_MAIN_CURRENCY, noBalanceIcon: true }, dataAttr: 'amount' },
			{ value: getCodedCardNumber(destinationCardId) ? getCodedCardNumber(destinationCardId) : '-', dataAttr: 'destinationCard' }
		]
	})
})

const getParameterValue = (paramsList, paramName) => {
	const targetParam = paramsList.find(param => param.name === paramName)
	return targetParam && targetParam.value ? targetParam.value : '-'
}

const getCodedCardNumber = cardId => {
	return props.getCardsList.find(card => card.id === cardId)
		? props.getCardsList.find(card => card.id === cardId).cardNumber
		: null
}

/*************************************************
*                                                *
*               Load More Favorites              *
*                                                *
*************************************************/
const tableLoadingMore = ref(false)
const favoritesPayload = computed(() => {
	return {
		operationTypes: 5004,
		pageSize: 10,
		pageNumber: 1
	}
})

const loadMoreFavorites = async () => {
	tableLoadingMore.value = true

	try {
		await requestFavoritesList(favoritesPayload.value)
		favoritesList.value = getFavoritesList.value
		tableLoadingMore.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, loadMoreFavorites)
	}
}

const loadMoreHandler = () => {
	favoritesPayload.value.pageNumber++
	loadMoreFavorites()
}
/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestFavoritesModalData = async () => {
	applyErrorState.value = false
	showLoader.value = true

	try {
		await requestFavoritesList(favoritesPayload.value)
		favoritesList.value = getFavoritesList.value
		parsedlatestTransfersList()
		showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, requestFavoritesModalData)
	}
}
/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlaySpinner.value = false
    applyErrorState.value = true

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestFavoritesModalData()
</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;
    width: 100%;
}
</style>
